import { motion, AnimatePresence } from 'motion/react';
import { useEffect, useRef, useState } from "react";
import { useTheme } from "../hooks/useTheme.jsx";
import { updateServiceWorker } from "../utils/cacheManager";
import { checkClient, checkInstance, getPOSPaymentMethod, watchPOSPaymentMethod } from "../utils/setupChecks.js";
import { pollPOS, pairTerminal, POLLABLE_POS_TYPES } from "../utils/posPolling.js";
import { Printer } from "../utils/Printer.js";
import { watchPrinterLink, validatePrinterLink, MIN_PRINTER_APP_VERSION_NAME } from "../utils/printerLink.js";
import { hasModule, PRINTER_BRIDGE_MODULE } from "../utils/modules.js";
import { PRINTER_APPS, PRINTER_APP_IDS, getCurrentPlatform, downloadPrinterApp } from "../utils/printerApps.js";
import { useAppReleases } from "../hooks/useAppReleases.js";
import { findCheapestProduct, runTestSale, requestTestSalePrint, watchTestSalePrint } from "../utils/testSale.js";
import Currency from "../utils/Currency.js";
import { doc, getDocFromServer } from "firebase/firestore";
import { db } from "../lib/firebase";
import { LoaderCircle, ChevronLeft } from 'lucide-react';
import brush from "../images/brush.svg";

const NETWORK_ERROR = "No se pudo conectar con el servidor. Revise la conexión e intente nuevamente.";
// Getnet tarda ~33 s en responder cuando el POS no contesta; el límite debe quedar por sobre eso.
const VALIDATION_TIMEOUT = 60 * 1000;

// Posibles causas cuando el POS no responde al polling, por proveedor.
const INVALID_MERCHANT_CAUSE = "Es posible que el dispositivo esté asignado a otro comercio; este es el caso cuando el POS muestra el error \"Comercio Inválido\". En dicho caso se debe comunicar con el proveedor correspondiente.";
// Posibles causas cuando falla la vinculación del terminal, por proveedor.
const PAIRING_ERROR_CAUSES = {
  Getnet: [
    "El número de serie ingresado no es correcto. Revíselo en la parte posterior del terminal.",
    "El terminal no pertenece a la cuenta de Getnet del comercio. En dicho caso se debe comunicar con el proveedor correspondiente.",
    "El tótem no tiene conexión a internet.",
  ],
  MercadoPago: [
    "El número de serie ingresado no es correcto. Revíselo en la parte posterior del terminal.",
    "El terminal no pertenece a la cuenta de Mercado Pago del comercio o fue eliminado de ella.",
    "El tótem no tiene conexión a internet.",
  ],
};

const POS_ERROR_CAUSES = {
  Getnet: [
    "El POS está apagado o no tiene una conexión a internet estable.",
    INVALID_MERCHANT_CAUSE,
    "El POS no está vinculado a esta instancia o a su tienda. Ingrese el ID del terminal y presione \"Vincular\".",
    "El tótem no logra comunicarse con el servicio de Getnet.",
  ],
  // El backend solo verifica que el terminal vinculado exista en la cuenta de MercadoPago.
  MercadoPago: [
    "El POS no está vinculado a esta instancia o el ID ingresado no corresponde a un terminal de la cuenta de Mercado Pago del comercio. Ingrese el ID del terminal y presione \"Vincular\".",
    "El terminal fue desvinculado o eliminado de la cuenta de Mercado Pago.",
    "El tótem no tiene conexión a internet o no logra comunicarse con el servicio de Mercado Pago.",
  ],
  // El POS Transbank se comunica con el tótem por cable USB; la prueba no requiere internet.
  Transbank: [
    "El POS está apagado.",
    "El POS no está conectado físicamente a este equipo.",
    "El servicio local de Transbank (https://localhost:5001) no está en ejecución en este equipo, o su configuración no fue realizada correctamente.",
  ],
};

// Si la página no pasa a segundo plano en este tiempo tras enviar el enlace, la app no se abrió.
const PRINTER_APP_OPEN_TIMEOUT = 3000;
// Tiempo de espera de la respuesta de la app una vez que el tótem vuelve a primer plano.
const PRINTER_APP_RESPONSE_TIMEOUT = 8000;

const PRINTER_LINK_ERRORS = {
  open_failed: {
    message: "No se pudo abrir la aplicación de impresión.",
    causes: [
      "La aplicación de impresión (Printer Bridge) no está instalada en este equipo.",
      `La aplicación de impresión está desactualizada y no reconoce el enlace de vinculación. Instale la versión ${MIN_PRINTER_APP_VERSION_NAME} o superior.`,
      "El navegador bloqueó la apertura de la aplicación. Si aparece un diálogo para abrir \"Printer Bridge\", acéptelo; si no, revise los permisos del navegador para abrir aplicaciones.",
      "Este equipo no es el tótem (por ejemplo, un computador), donde la aplicación de impresión no existe.",
    ],
  },
  no_response: {
    message: "No se recibió la configuración desde la aplicación de impresión.",
    causes: [
      "Se volvió al tótem sin presionar \"Volver al tótem\" en la aplicación de impresión.",
      `La aplicación de impresión está desactualizada (anterior a la versión ${MIN_PRINTER_APP_VERSION_NAME}) y no tiene el botón "Volver al tótem".`,
      "El equipo no tiene conexión a internet, por lo que la configuración no llegó.",
    ],
  },
  outdated: {
    message: `La aplicación de impresión está desactualizada. Instale la versión ${MIN_PRINTER_APP_VERSION_NAME} o superior.`,
    causes: [],
  },
  mismatch: {
    message: "La aplicación de impresión quedó configurada con otros datos (cliente, instancia o ambiente).",
    causes: ["Se modificaron los datos manualmente en \"Options\" de la aplicación de impresión después de vincular."],
    solutions: ["Presione \"Vincular impresora\" nuevamente para enviar los datos correctos."],
  },
  no_printer: {
    message: "La aplicación de impresión no tiene una impresora conectada.",
    causes: [
      "La impresora está apagada o su cable USB está desconectado.",
      "No se ha seleccionado la impresora en \"Printers\" dentro de la aplicación de impresión.",
      "No se aceptó el permiso de acceso USB que solicita Android. Presione \"Printers\" en la aplicación de impresión y seleccione la impresora para volver a solicitarlo.",
    ],
  },
  printing_disabled: {
    message: "La impresión está desactivada en la aplicación de impresión.",
    causes: [],
    solutions: ["Active el interruptor \"Printing Enabled\" en la aplicación de impresión y vincule nuevamente."],
  },
};

// Venta de prueba (paso 5): tiempo máximo de espera de la impresión una vez pagada.
const SALE_PRINT_TIMEOUT = 90 * 1000;

const SALE_ERRORS = {
  no_product: { message: "No hay productos con precio para la tienda de esta instancia.", causes: [] },
  checkout: {
    message: "No se pudo crear la venta.",
    causes: [
      "El producto no está disponible para la venta en esta tienda o su precio cambió.",
      "El tótem no tiene conexión a internet.",
    ],
  },
  payment: {
    message: "El pago no fue aprobado.",
    causes: [
      "Se canceló la operación en el POS o se agotó el tiempo para pasar la tarjeta.",
      "La tarjeta fue rechazada.",
    ],
    // Causas adicionales según el proveedor del método de pago.
    providerCauses: { Getnet: [INVALID_MERCHANT_CAUSE] },
  },
  timeout: {
    message: "Se agotó el tiempo de espera del pago en el POS.",
    causes: ["No se pasó la tarjeta a tiempo.", "El POS perdió la conexión durante el pago."],
  },
  print: {
    message: "La venta fue pagada, pero la boleta no se imprimió.",
    causes: [
      "La aplicación de impresión (Printer Bridge) no está abierta o perdió la conexión a internet.",
      "La impresora está apagada, sin papel o desconectada.",
      "La impresión está desactivada en la aplicación de impresión.",
    ],
  },
};

// Solo fuera de producción se permite omitir los pasos marcados como `skippable`, salvo los que
// además tienen `skippableInProduction` (la venta de prueba, que no siempre se puede hacer en remoto).
const CAN_SKIP_ENVS = ["development", "staging"];

const getStoreId = (instanceData) => instanceData.store_id || instanceData.store;

// Valor con el que el backend busca el terminal al vincular (`getTerminalByQuery`): el número de
// serie en Getnet y el `terminal_id` en MercadoPago (que termina en el número de serie).
const getLinkedTerminal = (instanceData) => instanceData?.terminal_data?.serialNumber || instanceData?.terminal_id || "";

async function loadPOSPaymentMethod(env, clientId, theme) {
  return toPOSMethodResult(await getPOSPaymentMethod(env, clientId, theme));
}

function toPOSMethodResult(check) {
  if (!check.ok) return { error: check.error === "no_pos_method" ? "El cliente no tiene métodos de pago con POS configurados." : NETWORK_ERROR };
  const method = check.data;
  if (!POLLABLE_POS_TYPES.includes(method.type_details)) {
    return { error: `El método de pago "${method.name} (${method.type_details})" no permite probar la conexión con el POS.` };
  }
  return { result: method };
}

// Cada paso valida un dato contra la base de datos (o un servicio) antes de avanzar.
// Los pasos con `input` piden un valor al usuario; los demás solo ejecutan su validación.
// `validate(value, data, ctx)` recibe el valor ingresado, lo acumulado en pasos anteriores y
// `{ env, theme, signal }`, y devuelve `{ error }` o `{ result }`; `result` queda guardado en
// `data[step.key]` y `summary(result)` lo resume en los pasos siguientes.
// `prepare(data, ctx)`, si existe, se ejecuta al entrar al paso y su `result` llega a `validate`
// (como `ctx.prepared`) y a `title(prepared)`. `watch(data, ctx, onResult)` es la versión en vivo:
// entrega un resultado cada vez que cambia y devuelve la función que cancela la escucha.
const STEPS = [
  {
    key: "client",
    successMessage: "El cliente ha sido configurado exitosamente.",
    label: "Cliente",
    input: { placeholder: "Escriba el ID del cliente..." },
    summary: (result) => result.id,
    validate: async (value, data, { env }) => {
      const check = await checkClient(env, value);
      if (!check.ok) return { error: check.error === "not_found" ? `El cliente "${value}" no existe.` : NETWORK_ERROR };
      return { result: { id: value, data: check.data } };
    },
  },
  {
    key: "instance",
    successMessage: "La instancia ha sido configurada exitosamente.",
    label: "Instancia",
    input: { placeholder: "Escriba el ID de la instancia..." },
    summary: (result) => result.id,
    validate: async (value, data, { env }) => {
      const check = await checkInstance(env, data.client.id, value);
      if (!check.ok) {
        if (check.error === "not_found") return { error: `La instancia "${value}" no existe para el cliente "${data.client.id}".` };
        if (check.error === "in_use") return { error: `La instancia "${value}" tiene una sesión activa en otro tótem. Cierre esa sesión antes de continuar.` };
        return { error: NETWORK_ERROR };
      }
      return { result: { id: value, data: check.data } };
    },
  },
  {
    key: "pos",
    successMessage: "La conexión con el POS ha sido verificada exitosamente.",
    label: "POS",
    title: (method) => `Conexión con ${method?.type_details || "POS"}`,
    centered: true,
    actionLabel: "Probar conexión",
    loadingMessage: "Probando la conexión con el POS, esto puede tardar hasta un minuto...",
    skippable: true,
    // Los POS sin terminal vinculado (Transbank) no tienen número de serie; se muestra el proveedor.
    summary: (result) => result.serialNumber || result.type_details,
    // Al omitir el paso se muestra el terminal ya vinculado a la instancia, si lo hay.
    skippedSummary: (data) => getLinkedTerminal(data.instance?.data) || null,
    // Se escucha en vivo: si en el dashboard se elige otro método para el asistente, el paso cambia.
    watch: (data, { env, theme }, onResult) => watchPOSPaymentMethod(env, data.client.id, theme, (check) => onResult(toPOSMethodResult(check))),
    initialValue: (data) => getLinkedTerminal(data.instance.data),
    validate: async (value, data, { env, theme, signal, prepared, linkedTerminal, onTerminalLinked }) => {
      let method = prepared;
      if (!method) {
        const load = await loadPOSPaymentMethod(env, data.client.id, theme);
        if (load.error) return load;
        method = load.result;
      }
      // Los POS `POSAPI` se comunican vía backend y requieren un terminal vinculado a la instancia.
      // Si el ID ingresado no es el ya vinculado, se vincula antes de probar la conexión.
      if (method.type === "POSAPI") {
        if (!value) return { error: "Ingrese el ID del terminal." };
        if (value !== linkedTerminal) {
          const pairing = await pairTerminal({ env, client: data.client.id, instanceId: data.instance.id, serialNumber: value });
          if (!pairing.ok) {
            console.warn("[SetupWizard] Terminal pairing failed:", pairing.message);
            return { error: { message: `No se pudo vincular el terminal "${value}".`, causes: PAIRING_ERROR_CAUSES[method.type_details] || [] } };
          }
          onTerminalLinked(value);
        }
      }
      const response = await pollPOS({
        env,
        client: data.client.id,
        instanceId: data.instance.id,
        storeId: getStoreId(data.instance.data),
        type: method.type_details,
        signal,
      });
      if (response.status !== 'OK') {
        console.warn("[SetupWizard] POS polling failed:", response);
        return {
          error: {
            message: `No se pudo conectar con el POS (${method.type_details}).`,
            causes: POS_ERROR_CAUSES[method.type_details] || [],
          },
        };
      }
      return { result: { ...method, serialNumber: method.type === "POSAPI" ? value : null } };
    },
  },
  {
    key: "printer",
    module: PRINTER_BRIDGE_MODULE,
    successMessage: "La impresora ha sido vinculada exitosamente.",
    label: "Impresora",
    title: () => "Vincular impresora",
    centered: true,
    description: "Presione \"Vincular impresora\" para establecer toda la configuración necesaria. Luego presione volver desde la aplicación de impresión.",
    skippable: true,
    summary: (result) => result.printer_name || "Vinculada",
    // El resultado lo entrega <PrinterLink> una vez validada la respuesta de la app de impresión.
    validate: async (value, data, { printerLink }) => {
      if (!printerLink) return { error: "Vincule la impresora antes de continuar." };
      return { result: printerLink };
    },
  },
  {
    key: "sale",
    successMessage: "La venta de prueba se ha realizado exitosamente.",
    label: "Venta",
    title: () => "Venta de prueba",
    centered: true,
    actionLabel: "Iniciar venta",
    skippable: true,
    skippableInProduction: true,
    summary: (result) => result.cartId,
    // Tras una venta exitosa, el operador confirma lo que el sistema no puede verificar solo. El
    // tótem se abre únicamente si todas las respuestas son "Sí".
    checklist: [
      {
        key: "printed",
        question: "¿Salió la impresión?",
        help: "Revise que la impresora esté encendida, tenga papel y que la aplicación Printer Bridge esté abierta. Puede volver al paso anterior para vincular la impresora nuevamente.",
      },
      {
        key: "right_kiosk",
        question: "¿Salió la impresión en el tótem que corresponde?",
        help: "La impresora de otro tótem está vinculada a esta instancia. Vuelva al paso \"Vincular impresora\" y vincule la impresora desde este tótem.",
      },
    ],
    // Producto de menor precio y método de pago (el del paso 3, o el del asistente si se omitió).
    prepare: async (data, { env, theme }) => {
      const instanceData = data.instance.data;
      const [productCheck, methodCheck, priceFormat] = await Promise.all([
        findCheapestProduct(env, data.client.id, getStoreId(instanceData)),
        data.pos ? Promise.resolve({ ok: true, data: data.pos }) : getPOSPaymentMethod(env, data.client.id, theme),
        getPriceFormat(env, data.client.id),
      ]);
      if (!productCheck.ok) return { error: productCheck.error === "no_product" ? SALE_ERRORS.no_product : NETWORK_ERROR };
      const method = toPOSMethodResult(methodCheck);
      if (method.error) return method;
      return { result: { ...productCheck.data, method: method.result, currency: new Currency(priceFormat.currency, priceFormat.locale) } };
    },
    validate: async (value, data, { env, prepared, onSaleStatus, steps }) => {
      if (!prepared) return { error: "No se pudo preparar la venta de prueba." };
      const instanceData = data.instance.data;
      const sale = await runTestSale({
        env,
        client: data.client.id,
        instanceId: data.instance.id,
        storeId: getStoreId(instanceData),
        method: prepared.method,
        product: prepared.product,
        price: prepared.price,
        store: prepared.store,
        clientData: data.client.data,
        terminalData: instanceData.terminal_data,
        onStatus: onSaleStatus,
      });
      if (!sale.ok) {
        console.warn("[SetupWizard] Test sale failed:", sale.error, sale.detail);
        const { providerCauses, ...saleError } = SALE_ERRORS[sale.error];
        return {
          error: {
            ...saleError,
            causes: [...saleError.causes, ...(providerCauses?.[prepared.method.type_details] || [])],
          },
        };
      }

      // Se imprime como una venta real. Si la impresora se vinculó en el paso 4, se espera a que
      // Printer Bridge marque la boleta como impresa.
      onSaleStatus("printing");
      try {
        await requestTestSalePrint(env, data.client.id, sale.cartId, data.client.data, sale.cart);
      } catch (error) {
        console.error("[SetupWizard] Error requesting print:", error);
        return { error: { ...SALE_ERRORS.print, detailLabel: "Venta", detail: sale.cartId } };
      }
      const waitsForPrinter = steps.some((s) => s.key === "printer") && !!data.printer;
      if (waitsForPrinter) {
        const printed = await new Promise((resolve) => {
          const timeout = setTimeout(() => { unsubscribe(); resolve(false); }, SALE_PRINT_TIMEOUT);
          const unsubscribe = watchTestSalePrint(env, data.client.id, sale.cartId, (status) => {
            if (status === "pending") return;
            clearTimeout(timeout);
            unsubscribe();
            resolve(status === "printed");
          });
        });
        if (!printed) return { error: { ...SALE_ERRORS.print, detailLabel: "Venta", detail: sale.cartId } };
      }
      return { result: { cartId: sale.cartId } };
    },
  },
];

async function getPriceFormat(env, clientId) {
  try {
    const styles = (await getDocFromServer(doc(db, "kiosk", env, "client", clientId, "config", "styles"))).data();
    const prices = styles?.global?.prices || styles?.dark?.global?.prices || {};
    return { currency: prices.currency || "CLP", locale: prices.locale || "es-CL" };
  } catch {
    return { currency: "CLP", locale: "es-CL" };
  }
}

// Preguntas de verificación con respuesta Sí/No. "Continuar" solo se habilita con todas en "Sí";
// si alguna es "No" se muestra qué revisar y el botón pasa a "Reintentar", que vuelve al paso.
function Checklist({ items, answers, onAnswer, onContinue, onRetry, disabled }) {
  const allYes = items.every((item) => answers[item.key] === true);
  const anyNo = items.some((item) => answers[item.key] === false);
  const option = (item, value, label) => {
    const selected = answers[item.key] === value;
    return (
      <button
        type="button"
        className="main-font-bold uppercase flex-1 p-2 rounded-[10px] cursor-pointer border-2 border-[#00a6f4]"
        style={selected ? { backgroundColor: "#00a6f4", color: "#000" } : { color: "#00a6f4" }}
        onClick={() => onAnswer(item.key, value)}
        disabled={disabled}
        aria-pressed={selected}
      >
        {label}
      </button>
    );
  };
  return (
    <motion.div className="mt-3" initial={{ opacity: 0, x: 30 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.2 }}>
      <p className="main-font-regular text-xl text-center">Verificación</p>
      <p className="secondary-font-regular text-base text-center mt-2">Confirme lo siguiente antes de abrir el tótem.</p>
      <div className="mt-6 space-y-5">
        {items.map((item) => (
          <div key={item.key}>
            <p className="secondary-font-bold text-base">{item.question}</p>
            <div className="flex gap-3 mt-2">
              {option(item, true, "Sí")}
              {option(item, false, "No")}
            </div>
            {answers[item.key] === false && <p className="secondary-font-regular text-sm text-red-500 mt-2">{item.help}</p>}
          </div>
        ))}
      </div>
      <motion.button
        type="button"
        whileTap={{ scale: 0.9 }}
        whileHover={{ scale: 1.03 }}
        className="main-font-bold uppercase w-full mt-8 p-3 rounded-[10px] cursor-pointer bg-[#00a6f4] disabled:bg-[#b8e6fe] disabled:text-[#AAAAAA]"
        onClick={anyNo ? onRetry : onContinue}
        disabled={disabled || (!anyNo && !allYes)}
      >
        {anyNo ? "Reintentar" : "Continuar"}
      </motion.button>
    </motion.div>
  );
}

const SALE_STATUS_TEXT = {
  creating: "Creando la venta...",
  at_terminal: "Pase la tarjeta en el POS para completar el pago.",
  paid: "Pago aprobado.",
  printing: "Imprimiendo la boleta...",
};

// Resumen de la venta de prueba: producto, precio y método, y el estado mientras se procesa.
function SaleSummary({ prepared, status }) {
  if (!prepared) return null;
  return (
    <div className="mt-4 text-center">
      <p className="secondary-font-regular text-base">
        Se realizará una venta <b>real</b> del producto de menor precio disponible para probar el pago y la impresión.
      </p>
      <div className="secondary-font-regular text-base mt-4 rounded-[10px] px-4 py-3" style={{ backgroundColor: "rgba(0, 166, 244, 0.12)", border: "1px solid rgba(0, 166, 244, 0.35)" }}>
        <p className="secondary-font-bold">{prepared.product.name || prepared.product.id}</p>
        <p>{prepared.currency.format(prepared.price)} · {prepared.method.type_details}</p>
      </div>
      {status && <p className="secondary-font-bold text-base mt-4">{SALE_STATUS_TEXT[status]}</p>}
    </div>
  );
}

// Vincula un terminal a la instancia por su número de serie, igual que "Conectar POS" del menú
// secreto. Solo se muestra para métodos `POSAPI`. El valor lo controla el asistente porque
// "Probar conexión" también lo usa.
function TerminalPairing({ env, clientId, instanceId, value, onChange, linkedTerminal, onLinked, onPairingChange, disabled }) {
  const [isPairing, setIsPairing] = useState(false);
  const [message, setMessage] = useState(null);
  const serialNumber = value.trim();
  const isLinked = !!serialNumber && serialNumber === linkedTerminal;

  const handlePair = async () => {
    if (!serialNumber || isPairing) return;
    setIsPairing(true);
    onPairingChange(true);
    setMessage(null);
    const result = await pairTerminal({ env, client: clientId, instanceId, serialNumber });
    setMessage(result.ok ? { type: "success", text: "Terminal vinculado exitosamente. Ahora pruebe la conexión." } : { type: "error", text: result.message });
    setIsPairing(false);
    onPairingChange(false);
    if (result.ok) onLinked(serialNumber);
  };

  const handleKeyDown = (e) => {
    // Evita que Enter envíe el formulario del paso (que probaría la conexión).
    if (e.key === "Enter") {
      e.preventDefault();
      handlePair();
    }
  };

  return (
    <div className="mt-4">
      <p className="secondary-font-regular text-base mb-4">
        Ingrese o escanee el número de serie del terminal que está conectado a este tótem. El número de serie está en la parte posterior del terminal, debajo del código de barras, generalmente donde dice S/N o SN.
      </p>
      <label htmlFor="terminal-serial" className="secondary-font-regular text-sm opacity-70">ID del terminal:</label>
      <div className="flex gap-3 mt-2">
        <input
          id="terminal-serial"
          type="text"
          // Sin autoFocus: el teclado en pantalla taparía las instrucciones del paso.
          autoComplete="off"
          placeholder="Ingrese el número de serie"
          className="main-font-regular flex-1 min-w-0 py-3 px-5 text-lg bg-white text-black border border-black rounded-[10px] appearance-none"
          value={value}
          onChange={(e) => { onChange(e.target.value); setMessage(null); }}
          onKeyDown={handleKeyDown}
          disabled={disabled || isPairing}
        />
        <motion.button
          type="button"
          whileTap={{ scale: 0.9 }}
          whileHover={{ scale: 1.03 }}
          className="main-font-bold uppercase px-5 min-w-[130px] rounded-[10px] cursor-pointer border-2 border-[#00a6f4] text-[#00a6f4] disabled:cursor-not-allowed"
          onClick={handlePair}
          disabled={disabled || isPairing || !serialNumber}
        >
          {isPairing ? <LoaderCircle className="animate-spin mx-auto" /> : "Vincular"}
        </motion.button>
      </div>
      {message ? (
        <p className={`secondary-font-bold text-sm text-center mt-2 ${message.type === "error" ? "text-red-500" : "text-green-600"}`}>{message.text}</p>
      ) : isLinked ? (
        <p className="secondary-font-regular text-sm text-center mt-2 opacity-70">Este terminal ya está vinculado a la instancia.</p>
      ) : null}
    </div>
  );
}

// Tiempo que se muestra la confirmación de un paso antes de avanzar.
const SUCCESS_ANIMATION_DURATION = 1600;
// Tiempo que se muestra la confirmación final antes de abrir el tótem.
const COMPLETION_ANIMATION_DURATION = 2500;

// Confirmación animada de un paso completado: círculo, check que se dibuja y el mensaje.
// `detail` agrega una línea secundaria y `progressDuration` (ms) una barra que se llena en ese tiempo.
function StepSuccess({ message, detail = null, progressDuration = null }) {
  return (
    <motion.div
      className="flex flex-col items-center justify-center py-8"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.2 }}
      role="status"
      aria-live="polite"
    >
      <motion.svg
        width="96"
        height="96"
        viewBox="0 0 96 96"
        initial={{ scale: 0.6 }}
        animate={{ scale: 1 }}
        transition={{ type: "spring", stiffness: 260, damping: 18 }}
      >
        <motion.circle
          cx="48"
          cy="48"
          r="44"
          fill="none"
          stroke="#16a34a"
          strokeWidth="6"
          initial={{ pathLength: 0 }}
          animate={{ pathLength: 1 }}
          transition={{ duration: 0.45, ease: "easeOut" }}
        />
        <motion.path
          d="M28 50 L42 64 L70 34"
          fill="none"
          stroke="#16a34a"
          strokeWidth="7"
          strokeLinecap="round"
          strokeLinejoin="round"
          initial={{ pathLength: 0 }}
          animate={{ pathLength: 1 }}
          transition={{ duration: 0.35, delay: 0.4, ease: "easeOut" }}
        />
      </motion.svg>
      <motion.p
        className="main-font-regular text-xl text-center mt-6"
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3, delay: 0.6 }}
      >
        {message}
      </motion.p>
      {detail && (
        <motion.p
          className="secondary-font-regular text-base text-center mt-2 opacity-70"
          initial={{ opacity: 0 }}
          animate={{ opacity: 0.7 }}
          transition={{ duration: 0.3, delay: 0.8 }}
        >
          {detail}
        </motion.p>
      )}
      {progressDuration && (
        <div className="w-2/3 h-1.5 mt-6 rounded-full overflow-hidden" style={{ backgroundColor: "rgba(0, 166, 244, 0.2)" }}>
          <motion.div
            className="h-full rounded-full"
            style={{ backgroundColor: "#00a6f4" }}
            initial={{ width: "0%" }}
            animate={{ width: "100%" }}
            transition={{ duration: progressDuration / 1000, ease: "linear" }}
          />
        </div>
      )}
    </motion.div>
  );
}

// Abre la app de impresión con los datos del tótem y espera su respuesta en Firestore (ver
// utils/printerLink.js). Detecta si la app no llegó a abrirse (la página nunca pasó a segundo
// plano) o si se volvió al tótem sin que la app respondiera.
function PrinterLink({ env, clientId, instanceId, disabled, onLinked, onError }) {
  const [status, setStatus] = useState("idle"); // idle | opening | waiting | linked
  const unsubscribe = useRef(null);
  const timers = useRef([]);
  const cleanupListeners = useRef(null);

  const clearTimers = () => {
    timers.current.forEach(clearTimeout);
    timers.current = [];
  };

  const stopAll = () => {
    clearTimers();
    unsubscribe.current?.();
    unsubscribe.current = null;
    cleanupListeners.current?.();
    cleanupListeners.current = null;
  };

  // Solo al desmontar; `stopAll` trabaja sobre refs, así que no importa qué render la creó.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => stopAll, []);

  const handleLink = async () => {
    stopAll();
    onLinked(null);
    onError(null);
    setStatus("opening");
    const requestId = crypto.randomUUID();
    let opened = false;
    // Chrome puede entregar la respuesta mientras la página aún está en segundo plano, antes del
    // evento de visibilidad; una vez recibida, nada debe reemplazarla por "no se recibió".
    let responded = false;

    // La respuesta se sigue escuchando aunque haya fallado un intento, por si llega tarde.
    unsubscribe.current = watchPrinterLink(env, clientId, instanceId, requestId, (printerStatus) => {
      responded = true;
      clearTimers();
      const check = validatePrinterLink(printerStatus, { env, clientId, instanceId });
      if (check.ok) {
        setStatus("linked");
        onError(null);
        onLinked(printerStatus);
      } else {
        setStatus("idle");
        const known = PRINTER_LINK_ERRORS[check.error];
        onError(check.error === "mismatch"
          ? { ...known, detailLabel: "Datos en la aplicación de impresión", detail: `${printerStatus.client_id} / ${printerStatus.instance_id} / ${printerStatus.environment}` }
          : { ...known, detailLabel: "Versión de la aplicación de impresión", detail: printerStatus.app_version || null });
      }
    });

    const onHidden = () => {
      if (!document.hidden) return;
      opened = true;
      if (responded) return;
      clearTimers();
      setStatus("waiting");
      onError(null);
    };
    const onVisible = () => {
      if (document.hidden || !opened || responded) return;
      clearTimers();
      timers.current.push(setTimeout(() => {
        if (responded) return;
        setStatus("idle");
        onError(PRINTER_LINK_ERRORS.no_response);
      }, PRINTER_APP_RESPONSE_TIMEOUT));
    };
    const onVisibilityChange = () => (document.hidden ? onHidden() : onVisible());
    document.addEventListener("visibilitychange", onVisibilityChange);
    cleanupListeners.current = () => document.removeEventListener("visibilitychange", onVisibilityChange);

    const sent = await Printer.sendLinkIntent(clientId, instanceId, env, requestId);
    if (!sent) {
      setStatus("idle");
      onError(PRINTER_LINK_ERRORS.open_failed);
      return;
    }
    timers.current.push(setTimeout(() => {
      if (opened || responded) return;
      setStatus("idle");
      onError(PRINTER_LINK_ERRORS.open_failed);
    }, PRINTER_APP_OPEN_TIMEOUT));
  };

  const isBusy = status === "opening" || status === "waiting";

  return (
    <div className="mt-6">
      <motion.button
        type="button"
        whileTap={{ scale: 0.9 }}
        whileHover={{ scale: 1.03 }}
        className="main-font-bold uppercase w-full p-3 rounded-[10px] cursor-pointer border-2 border-[#00a6f4] text-[#00a6f4] disabled:cursor-not-allowed"
        onClick={handleLink}
        disabled={disabled || isBusy}
      >
        {isBusy ? <LoaderCircle className="animate-spin mx-auto" /> : status === "linked" ? "Vincular impresora nuevamente" : "Vincular impresora"}
      </motion.button>
      {status === "opening" && <p className="secondary-font-regular text-sm text-center mt-2 opacity-70">Abriendo la aplicación de impresión...</p>}
      {status === "waiting" && <p className="secondary-font-regular text-sm text-center mt-2 opacity-70">Esperando la respuesta de la aplicación de impresión...</p>}
      {status === "linked" && <p className="secondary-font-bold text-sm text-center mt-2 text-green-600">Impresora vinculada correctamente.</p>}
    </div>
  );
}

// Links a la versión estable del puente de impresión (MD Operaciones → Apps), por si no está instalado.
function PrinterAppDownloads({ disabled }) {
  const { loadedReleases, releases } = useAppReleases(PRINTER_APP_IDS);
  const currentPlatform = getCurrentPlatform();

  return (
    <div className="mt-6">
      <p className="secondary-font-regular text-sm text-center opacity-70">¿No tiene instalada la aplicación de impresión? Descárguela aquí:</p>
      <div className="flex gap-3 mt-3">
        {PRINTER_APPS.map((app) => {
          const stable = releases[app.id]?.stable;
          const isCurrent = app.platform === currentPlatform;
          return (
            <motion.button
              key={app.id}
              type="button"
              whileTap={{ scale: 0.9 }}
              whileHover={{ scale: 1.03 }}
              className={`secondary-font-bold flex-1 p-2 rounded-[10px] cursor-pointer border-2 border-[#00a6f4] text-sm disabled:cursor-not-allowed disabled:opacity-50 ${isCurrent ? "bg-[#00a6f4] text-black" : "text-[#00a6f4]"}`}
              onClick={() => downloadPrinterApp(stable)}
              disabled={disabled || !stable?.url}
            >
              {!loadedReleases ? <LoaderCircle className="animate-spin mx-auto" size={20} /> : stable?.url ? `${app.label} · v${stable.version}` : `${app.label} · No hay versión publicada`}
            </motion.button>
          );
        })}
      </div>
    </div>
  );
}

export function SetupWizard({ env, onConfigFinished }) {
  const [stepIndex, setStepIndex] = useState(0);
  const [values, setValues] = useState({});
  const [data, setData] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  // Mensaje del paso recién completado; mientras existe se muestra <StepSuccess> en lugar del formulario.
  const [successMessage, setSuccessMessage] = useState(null);
  // Todos los pasos terminaron; se muestra la confirmación final mientras se abre el tótem.
  const [isComplete, setIsComplete] = useState(false);
  const abortController = useRef(null);
  const { theme, setTheme } = useTheme();

  // Los pasos con `module` solo aplican si el cliente tiene ese módulo contratado. Mientras no se
  // conoce el cliente (paso 1) se muestran todos.
  const steps = STEPS.filter((s) => !s.module || !data.client || hasModule(data.client.data, s.module));
  const step = steps[stepIndex];
  const isLastStep = stepIndex === steps.length - 1;
  const value = values[step.key] || "";

  const [prepared, setPrepared] = useState(null);
  const [isPreparing, setIsPreparing] = useState(false);
  // Terminal vinculado a la instancia según el backend (o el último vinculado en este paso).
  const [linkedTerminal, setLinkedTerminal] = useState("");
  const [isPairing, setIsPairing] = useState(false);
  // Configuración validada que reportó la app de impresión (paso "printer").
  const [printerLink, setPrinterLink] = useState(null);

  useEffect(() => {
    return () => abortController.current?.abort();
  }, []);

  // Estado de la venta de prueba en curso (paso "sale").
  const [saleStatus, setSaleStatus] = useState(null);
  // Verificación pendiente tras completar un paso con `checklist` ({ data }) y sus respuestas.
  const [pendingChecklist, setPendingChecklist] = useState(null);
  const [checklistAnswers, setChecklistAnswers] = useState({});

  useEffect(() => {
    setPrinterLink(null);
    setSaleStatus(null);
    setPendingChecklist(null);
  }, [stepIndex]);

  // Al entrar a un paso con valor inicial (p. ej. el terminal ya vinculado), se precarga. Se
  // recalcula siempre porque depende de lo elegido en pasos anteriores.
  useEffect(() => {
    if (!step.initialValue) return;
    const initial = step.initialValue(data);
    setValues((current) => ({ ...current, [step.key]: initial }));
    setLinkedTerminal(initial);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stepIndex]);

  useEffect(() => {
    setPrepared(null);
    if (step.watch) {
      setIsPreparing(true);
      let currentKey;
      const unsubscribe = step.watch(data, { env, theme }, ({ error: prepareError, result }) => {
        // Solo reacciona si cambió el resultado (otro método o un error nuevo), para no borrar
        // el resultado de una prueba cada vez que llega una copia igual del documento.
        const key = result ? `${result.type}|${result.type_details}` : `error|${prepareError}`;
        if (key === currentKey) return;
        currentKey = key;
        setPrepared(result || null);
        setError(prepareError || null);
        setIsPreparing(false);
      });
      return () => unsubscribe();
    }
    if (!step.prepare) return;
    let cancelled = false;
    setIsPreparing(true);
    step.prepare(data, { env, theme }).then(({ error: prepareError, result }) => {
      if (cancelled) return;
      setPrepared(result || null);
      if (prepareError) setError(prepareError);
      setIsPreparing(false);
    });
    return () => { cancelled = true; };
    // Solo al entrar al paso o cambiar de tema (cada tema tiene su lista de métodos de pago).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stepIndex, theme]);

  const handleChange = (e) => {
    setValues({ ...values, [step.key]: e.target.value });
    setError(null);
  };

  const handleBack = () => {
    if (isLoading || isPairing) return;
    // Desde la verificación se vuelve al mismo paso (no al anterior).
    if (pendingChecklist) {
      setPendingChecklist(null);
      return;
    }
    if (stepIndex === 0) return;
    setError(null);
    setStepIndex(stepIndex - 1);
  };

  // Vuelve al paso que pidió la verificación (la venta de prueba) para repetirlo.
  const handleChecklistRetry = () => {
    setPendingChecklist(null);
    setChecklistAnswers({});
    setSaleStatus(null);
    setError(null);
  };

  const handleChecklistContinue = async () => {
    if (!pendingChecklist) return;
    const { data: finalData } = pendingChecklist;
    setIsLoading(true);
    if (isLastStep) {
      await finish(finalData);
      setIsLoading(false);
    } else {
      setPendingChecklist(null);
      setIsLoading(false);
      setStepIndex(stepIndex + 1);
    }
  };

  const finish = async (finalData) => {
    try {
      await updateServiceWorker();
    } catch (error) {
      console.error('[SetupWizard] Error updating service worker (ignored):', error);
    }
    setIsComplete(true);
    await new Promise((resolve) => setTimeout(resolve, COMPLETION_ANIMATION_DURATION));
    const instanceData = finalData.instance.data;
    onConfigFinished({ ...instanceData, client: finalData.client.id, instance: finalData.instance.id, storeId: getStoreId(instanceData) });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const trimmed = value.trim();
    if (step.input && !trimmed) {
      setError(`Por favor, ingrese el ID de ${step.label.toLowerCase()}.`);
      return;
    }
    setIsLoading(true);
    setError(null);
    abortController.current = new AbortController();
    const timeout = setTimeout(() => abortController.current?.abort(), VALIDATION_TIMEOUT);
    const { error: stepError, result } = await step.validate(trimmed, data, {
      env,
      theme,
      signal: abortController.current.signal,
      prepared,
      linkedTerminal,
      onTerminalLinked: setLinkedTerminal,
      printerLink,
      onSaleStatus: setSaleStatus,
      steps,
    });
    if (stepError) setSaleStatus(null);
    clearTimeout(timeout);
    abortController.current = null;
    if (stepError) {
      setError(stepError);
      setIsLoading(false);
      return;
    }
    // Los pasos siguientes dependen de este: si cambió, se descartan sus resultados.
    const newData = { ...Object.fromEntries(steps.slice(0, stepIndex).map(s => [s.key, data[s.key]])), [step.key]: result };
    setData(newData);
    if (step.successMessage) {
      setSuccessMessage(step.successMessage);
      await new Promise((resolve) => setTimeout(resolve, SUCCESS_ANIMATION_DURATION));
      setSuccessMessage(null);
    }
    if (step.checklist) {
      // Se espera la verificación del operador; "Continuar" en <Checklist> sigue desde aquí.
      setChecklistAnswers({});
      setPendingChecklist({ data: newData });
      setIsLoading(false);
      return;
    }
    if (isLastStep) {
      await finish(newData);
      setIsLoading(false);
    } else {
      setIsLoading(false);
      setStepIndex(stepIndex + 1);
    }
  };

  const canSkip = step.skippable && (CAN_SKIP_ENVS.includes(env) || step.skippableInProduction);
  const needsTerminal = step.key === "pos" && prepared?.type === "POSAPI";

  const handleSkip = async () => {
    if (isLoading || isPairing || !canSkip) return;
    setError(null);
    const newData = { ...Object.fromEntries(steps.slice(0, stepIndex).map(s => [s.key, data[s.key]])), [step.key]: null };
    setData(newData);
    if (isLastStep) {
      setIsLoading(true);
      await finish(newData);
      setIsLoading(false);
    } else {
      setStepIndex(stepIndex + 1);
    }
  };

  const submitLabel = step.actionLabel || (isLastStep ? "Iniciar" : "Continuar");
  const errorMessage = typeof error === "string" ? error : error?.message;

  return (
    <div className="w-screen h-screen flex justify-center items-center">
      <div className="relative w-[500px] max-w-[90%] max-h-[95vh] overflow-y-auto border-2 border-white p-8 rounded-[10px] shadow-2xl">
        <h1 className="main-font-bold w-full text-center uppercase mb-6 text-3xl" style={{color: "#00a6f4"}}>Self-Ordering Kiosk</h1>
        <motion.div
          className="absolute top-7 right-4 w-10 h-10 rounded-full p-3 cursor-pointer"
          style={{backgroundColor: "#d1d5dc"}}
          whileTap={{ scale: 0.9 }}
          whileHover={{ scale: 1.1 }}
          onClick={() => setTheme(theme === 'light' ? 'dark' : 'light')}
        >
          <img className="w-full h-full cursor-pointer" src={brush}/>
        </motion.div>
        {stepIndex > 0 && (
          <motion.button
            type="button"
            className="absolute top-7 left-4 w-10 h-10 rounded-full p-2 cursor-pointer text-black disabled:opacity-50 disabled:cursor-default"
            style={{backgroundColor: "#d1d5dc"}}
            whileTap={{ scale: 0.9 }}
            whileHover={{ scale: 1.1 }}
            onClick={handleBack}
            disabled={isLoading || isPairing}
            aria-label="Volver"
          >
            <ChevronLeft className="w-full h-full" />
          </motion.button>
        )}

        <div className="flex items-center justify-center gap-2 mb-2">
          {steps.map((s, index) => (
            <div
              key={s.key}
              className="h-2 rounded-full transition-all"
              style={{ width: index === stepIndex ? 32 : 12, backgroundColor: index <= stepIndex ? "#00a6f4" : "#d1d5dc" }}
            />
          ))}
        </div>
        <p className="secondary-font-regular text-center text-sm opacity-70 mb-4">Paso {stepIndex + 1} de {steps.length}</p>

        {stepIndex > 0 && (
          <div className="flex flex-wrap justify-center gap-2 mt-8 mb-6">
            {steps.slice(0, stepIndex).map(s => (
              <p
                key={s.key}
                className="secondary-font-regular text-sm px-3 py-1 rounded-full max-w-full truncate"
                style={{backgroundColor: "rgba(0, 166, 244, 0.12)", border: "1px solid rgba(0, 166, 244, 0.35)"}}
              >
                <span className="opacity-70">{s.label}:</span> <b>{data[s.key] ? s.summary(data[s.key]) : data[s.key] === null ? (s.skippedSummary?.(data) || "Omitido") : null}</b>
              </p>
            ))}
          </div>
        )}

        {isComplete ? (
          <StepSuccess
            key="complete"
            message="¡La configuración ha sido completada!"
            detail="El tótem se abrirá en unos segundos..."
            progressDuration={COMPLETION_ANIMATION_DURATION}
          />
        ) : pendingChecklist && step.checklist ? (
          <Checklist
            items={step.checklist}
            answers={checklistAnswers}
            onAnswer={(key, value) => setChecklistAnswers((current) => ({ ...current, [key]: value }))}
            onContinue={handleChecklistContinue}
            onRetry={handleChecklistRetry}
            disabled={isLoading}
          />
        ) : successMessage ? (
          <StepSuccess key={successMessage} message={successMessage} />
        ) : (
        <form onSubmit={handleSubmit}>
          <AnimatePresence mode="wait">
            <motion.div
              key={step.key}
              className="mt-3"
              initial={{ opacity: 0, x: 30 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -30 }}
              transition={{ duration: 0.2 }}
            >
              <label htmlFor={step.key} className={`main-font-regular text-xl block ${step.centered ? "text-center" : ""}`}>
                {step.title ? step.title(prepared) : `${step.label}:`}
              </label>
              {step.input ? (
                <input
                  id={step.key}
                  type="text"
                  // Sin autoFocus: el teclado en pantalla taparía el asistente antes de leerlo.
                  autoComplete="off"
                  placeholder={step.input.placeholder}
                  className="main-font-regular w-full mt-3 py-3 px-5 text-lg bg-white text-black border border-black rounded-[10px] appearance-none"
                  value={value}
                  onChange={handleChange}
                  disabled={isLoading}
                />
              ) : step.description ? (
                <p id={step.key} className="secondary-font-regular mt-3 text-base">{step.description}</p>
              ) : null}
              {step.key === "sale" && <SaleSummary prepared={prepared} status={saleStatus} />}
              {needsTerminal && (
                <TerminalPairing
                  env={env}
                  clientId={data.client.id}
                  instanceId={data.instance.id}
                  value={value}
                  onChange={(newValue) => { setValues({ ...values, [step.key]: newValue }); setError(null); }}
                  linkedTerminal={linkedTerminal}
                  onLinked={(serial) => { setLinkedTerminal(serial); setError(null); }}
                  onPairingChange={setIsPairing}
                  disabled={isLoading}
                />
              )}
            </motion.div>
          </AnimatePresence>
          {step.key === "printer" && (
            <PrinterLink
              env={env}
              clientId={data.client.id}
              instanceId={data.instance.id}
              disabled={isLoading}
              onLinked={setPrinterLink}
              onError={setError}
            />
          )}
          <div className={`flex gap-3 ${step.key === "printer" ? "mt-3" : "mt-8"}`}>
            <motion.button
              type="submit"
              whileTap={{ scale: 0.9 }}
              whileHover={{ scale: 1.03 }}
              className="main-font-bold uppercase flex-1 p-3 rounded-[10px] cursor-pointer bg-[#00a6f4] disabled:bg-[#b8e6fe] disabled:text-[#AAAAAA]"
              disabled={isLoading || isPreparing || isPairing || (needsTerminal && !value.trim()) || (step.key === "printer" && !printerLink)}
            >
              {isLoading ? <LoaderCircle className="animate-spin mx-auto" /> : submitLabel}
            </motion.button>
          </div>
          {isLoading && step.loadingMessage && <p className="secondary-font-regular text-center text-sm opacity-70 mt-4">{step.loadingMessage}</p>}
          {/* En la primera pantalla: la app de impresión se instala antes de configurar el tótem. */}
          {step.key === "client" && <PrinterAppDownloads disabled={isLoading} />}
          {canSkip && (
            <button
              type="button"
              className="secondary-font-regular w-full mt-3 text-sm underline opacity-70 cursor-pointer disabled:opacity-40"
              onClick={handleSkip}
              disabled={isLoading || isPairing}
            >
              Omitir este paso
            </button>
          )}
          {error && (
            <div className="mt-4">
              <p className="text-red-500 secondary-font-bold text-center">{errorMessage}</p>
              {error.detail && <p className="secondary-font-regular text-sm text-center mt-2 opacity-80">{error.detailLabel}: {error.detail}</p>}
              {error.causes?.length > 0 && (
                <div className="secondary-font-regular text-sm mt-4">
                  <p className="secondary-font-bold mb-1">Posibles causas:</p>
                  <ul className="list-disc pl-5 space-y-1">
                    {error.causes.map((cause) => <li key={cause}>{cause}</li>)}
                  </ul>
                </div>
              )}
              {error.solutions?.length > 0 && (
                <div className="secondary-font-regular text-sm mt-4">
                  <p className="secondary-font-bold mb-1">Solución:</p>
                  <ul className="list-disc pl-5 space-y-1">
                    {error.solutions.map((solution) => <li key={solution}>{solution}</li>)}
                  </ul>
                </div>
              )}
            </div>
          )}
        </form>
        )}
      </div>
    </div>
  );
}
